import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PayslipDetailView from '../../../components/payroll/PayslipDetailView';
import { payslipsApi } from '../../../api/payslipsApi';

import { toast } from 'react-hot-toast';

const PayslipEmployeeView = () => {
  const { batchId, payslipId } = useParams();
  const navigate = useNavigate();

  const { data: payslip, isLoading, error } = useQuery({
    queryKey: ['payslip', payslipId],
    queryFn: async () => {
      const response = await payslipsApi.getPayslipById(payslipId);
      if (response?.responseCode === 200 && response?.data) {
        return response.data;
      }
      if (response && !response.responseCode) {
        return response;
      }
      throw new Error(response?.responseMessage || 'Failed to load payslip');
    },
    enabled: !!payslipId,
    staleTime: 0
  });

  const handleBack = () => {
    if (batchId) {
      navigate(`/payslips/batch/${batchId}`, { replace: true });
    } else {
      navigate('/payslips');
    }
  };

  const handleDownload = async () => {
    if (!payslip) return;
    try {
      const idToDownload = payslip._id || payslip.id || payslipId;
      const response = await payslipsApi.downloadPayslip(idToDownload);
      
      // Depending on axios/interceptor setup, blob could be response or response.data
      const blob = response.data instanceof Blob ? response.data : (response instanceof Blob ? response : new Blob([response]));
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Fallback filename
      let fileName = `Payslip_${payslip.employeeName?.replace(/\s+/g, '_') || 'Employee'}_${payslip.employeeId || payslipId}.pdf`;
      
      // Try to get filename from content-disposition header if available
      if (response.headers && response.headers['content-disposition']) {
        const match = response.headers['content-disposition'].match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          fileName = match[1];
        }
      }
      
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Payslip downloaded successfully!');
    } catch (err) {
      toast.error(err?.message || 'Failed to download payslip');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4 flex items-center justify-between gap-4">
        <Button variant="outline" icon={<ArrowLeft className="h-4 w-4" />} onClick={handleBack}>
          Back to batch
        </Button>
        {payslip && (
          <Button variant="primary" icon={<Download className="h-4 w-4" />} onClick={handleDownload}>
            Download Payslip
          </Button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {isLoading && (
          <div className="flex items-center justify-center py-24 text-gray-500">
            <Loader2 className="h-8 w-8 animate-spin mr-2" />
            Loading payslip...
          </div>
        )}
        {error && !isLoading && (
          <div className="py-16 text-center text-red-600">
            {error.message || 'Failed to load payslip'}
          </div>
        )}
        {!isLoading && !error && payslip && <PayslipDetailView payslip={payslip} />}
      </div>
    </div>
  );
};

export default PayslipEmployeeView;
